/**
 * brand-directory-deeplink.js
 * ------------------------------------------------------------------
 * Makes the brand directory filters linkable:
 *
 *   /pages/perfume-brands?filter=arabic
 *   /pages/perfume-brands?filter=luxury
 *   /pages/perfume-brands?q=lattafa
 *   /pages/perfume-brands?filter=arabic&q=af
 *
 * Deliberately ADDITIVE. It does not modify the directory's own script
 * in sections/main-all-brands.liquid — that script keeps its state in a
 * closure we can't reach, so instead this drives it through the DOM it
 * already listens to (clicking a filter button, dispatching an input
 * event). That keeps a 34KB Liquid file untouched.
 *
 * INSTALL
 *   1. Shopify admin → Online Store → Themes → ⋯ → Edit code
 *   2. Assets → Add a new asset → upload this file
 *   3. layout/theme.liquid — add this line in <head>, directly after
 *      the existing cart-count-sync.js tag:
 *
 *      <script src="{{ 'brand-directory-deeplink.js' | asset_url }}" defer="defer"></script>
 *
 * Loaded globally; exits immediately on any page with no brand directory.
 */
(() => {
  if (window.brandDirectoryDeeplinkInit) return;
  window.brandDirectoryDeeplinkInit = true;

  const VALID = ['all', 'arabic', 'luxury'];

  const start = () => {
    const root = document.querySelector('[data-all-brands]');
    if (!root) return;

    const input = root.querySelector('[data-brand-search]');
    const buttons = [...root.querySelectorAll('[data-brand-filter]')];
    const tools = root.querySelector('.da-brands__tools');
    if (!buttons.length) return;

    /** Current filter, read from the DOM rather than any private state. */
    const currentFilter = () =>
      buttons.find((b) => b.getAttribute('aria-pressed') === 'true')?.dataset.brandFilter || 'all';

    const writeUrl = (filter, query) => {
      const p = new URLSearchParams(location.search);

      if (!filter || filter === 'all') p.delete('filter');
      else p.set('filter', filter);

      const q = (query || '').trim();
      if (q) p.set('q', q);
      else p.delete('q');

      p.delete('range'); // legacy alias, accepted on read, never written back

      const qs = p.toString();
      history.replaceState(null, '', qs ? `${location.pathname}?${qs}` : location.pathname);
    };

    /** Apply URL state by driving the directory's own event handlers. */
    const applyFromUrl = () => {
      const p = new URLSearchParams(location.search);
      const f = (p.get('filter') || p.get('range') || '').toLowerCase();
      const q = p.get('q') || '';
      let touched = false;

      if (input && q && input.value !== q) {
        input.value = q;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        touched = true;
      }

      if (VALID.includes(f) && f !== currentFilter()) {
        buttons.find((b) => b.dataset.brandFilter === f)?.click();
        touched = true;
      }

      return touched || VALID.includes(f) || Boolean(q);
    };

    // Keep the URL in step with whatever the shopper does.
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        writeUrl(button.dataset.brandFilter, input?.value);
      });
    });

    let debounce;
    input?.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => writeUrl(currentFilter(), input.value), 250);
    });

    root.querySelector('[data-brand-clear]')?.addEventListener('click', () => {
      setTimeout(() => writeUrl(currentFilter(), ''), 0);
    });

    root.querySelector('[data-brand-reset]')?.addEventListener('click', () => {
      setTimeout(() => writeUrl('all', ''), 0);
    });

    window.addEventListener('popstate', applyFromUrl);

    // Initial load: honour the incoming link.
    const cameFromLink = applyFromUrl();

    // A shared link should land on the results, not the top of the hero.
    // Delete this block if you'd rather the page didn't move.
    if (cameFromLink && tools && !location.hash) {
      requestAnimationFrame(() => tools.scrollIntoView({ block: 'start' }));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();